/**
 * Collection — AJAX filtering, sorting, pagination, quick add.
 * Targeted morph (grid, toolbar, pagination only) so the filter drawer stays open.
 */
import { Component } from '@theme/component';
import { fetchConfig, debounce } from '@theme/utilities';
import { CartAddEvent } from '@theme/events';

class CollectionComponent extends Component {
  #abortController = null;
  #infiniteObserver = null;

  get sectionId() {
    return this.dataset.sectionId;
  }

  get paginationType() {
    return this.dataset.paginationType || 'pagination';
  }

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('change', this.#onFilterChange);
    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeydown);
    this.#syncExpanded();
    // Outside-click close on document: clicks above the toolbar never reach this element.
    document.addEventListener('click', this.#onDocumentClick);

    if (this.paginationType === 'infinite') {
      this.#initInfiniteScroll();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#infiniteObserver?.disconnect();
    document.removeEventListener('click', this.#onDocumentClick);
  }

  // Close any open filter/sort dropdown when clicking outside of it (anywhere on the page).
  #onDocumentClick = (event) => {
    if (event.target.closest('.collection-dropdown')) return; // on a toggle or inside a panel
    this.#closeAllDropdowns();
  };

  // ── Filter/sort change ──

  #onFilterChange = debounce((event) => {
    const t = event.target;
    if (
      t.matches('[data-filter-checkbox]') ||
      t.matches('[data-input-min]') ||
      t.matches('[data-input-max]') ||
      t.matches('[data-range-min]') ||
      t.matches('[data-range-max]') ||
      t.matches('[data-sort-select]')
    ) {
      this.#fetchAndUpdate(this.#buildURL(), { scroll: false });
    }
  }, 350);

  // ── Click delegation ──

  #onClick = (event) => {
    const target = event.target;

    // Dropdown close button (mobile)
    const closeBtn = target.closest('[data-dropdown-close]');
    if (closeBtn) {
      this.#closeAllDropdowns();
      return;
    }

    // Dropdown toggle (inline filters)
    const toggle = target.closest('[data-dropdown-toggle]');
    if (toggle) {
      const panel = toggle.nextElementSibling;
      if (!panel) return;
      const wasOpen = panel.classList.contains('is-open');
      this.#closeAllDropdowns();
      if (!wasOpen) panel.classList.add('is-open');
      this.#syncExpanded();
      return;
    }

    // Pagination links
    const pageLink = target.closest('[data-pagination-link]');
    if (pageLink) { event.preventDefault(); this.#fetchAndUpdate(pageLink.href); return; }

    // Filter remove pills — update in place, like the checkbox filters (no scroll jump)
    const removeLink = target.closest('[data-filter-remove]');
    if (removeLink) { event.preventDefault(); this.#fetchAndUpdate(removeLink.href, { scroll: false }); return; }

    // Clear all filters — update in place (no scroll jump)
    const clearLink = target.closest('[data-clear-all]');
    if (clearLink) { event.preventDefault(); this.#fetchAndUpdate(clearLink.href, { scroll: false }); return; }

    // Load more
    const loadMore = target.closest('[data-load-more]');
    if (loadMore) { this.#onLoadMore(loadMore); return; }

    // Quick add
    const quickAdd = target.closest('[data-quick-add]');
    if (quickAdd) { event.preventDefault(); this.#onQuickAdd(quickAdd); return; }
  };

  #closeAllDropdowns() {
    this.querySelectorAll('[data-dropdown-panel]').forEach(p => p.classList.remove('is-open'));
    this.#syncExpanded();
  }

  /** État ouvert / fermé des menus filtres et tri, lu par les lecteurs d'écran. */
  #syncExpanded() {
    this.querySelectorAll('[data-dropdown-toggle]').forEach((toggle) => {
      const open = !!toggle.nextElementSibling?.classList.contains('is-open');
      toggle.setAttribute('aria-expanded', String(open));
    });
  }

  /** Échap ferme le menu ouvert et rend le focus à son bouton. */
  #onKeydown = (event) => {
    if (event.key !== 'Escape') return;
    const panel = this.querySelector('[data-dropdown-panel].is-open');
    if (!panel) return;
    const toggle = panel.previousElementSibling;
    this.#closeAllDropdowns();
    if (toggle?.matches('[data-dropdown-toggle]')) toggle.focus();
  };

  #getOpenDropdownIndex() {
    const panels = this.querySelectorAll('[data-dropdown-panel]');
    for (let i = 0; i < panels.length; i++) {
      if (panels[i].classList.contains('is-open')) return i;
    }
    return -1;
  }

  // ── Load More ──

  async #onLoadMore(btn) {
    const nextUrl = btn.dataset.nextUrl;
    if (!nextUrl) return;

    btn.disabled = true;
    btn.textContent = '...';

    try {
      const html = await this.#fetchSection(nextUrl);
      // Page quittée entre-temps (navigation sans rechargement) : ne pas réécrire l'URL de la nouvelle page.
      if (!html || !this.isConnected) return;

      const doc = new DOMParser().parseFromString(html, 'text/html');

      const newGrid = doc.querySelector('[data-product-grid]');
      const currentGrid = this.querySelector('[data-product-grid]');
      if (newGrid && currentGrid) {
        currentGrid.insertAdjacentHTML('beforeend', newGrid.innerHTML);
      }

      this.#replaceElement('[data-pagination]', doc);

      if (this.paginationType === 'infinite') {
        this.#infiniteObserver?.disconnect();
        this.#initInfiniteScroll();
      }

      window.history.replaceState({}, '', nextUrl);
    } catch (err) {
      console.error('Load more error:', err);
      btn.disabled = false;
    }
  }

  // ── Infinite Scroll ──

  #initInfiniteScroll() {
    const btn = this.querySelector('[data-load-more][data-infinite]');
    if (!btn) return;

    this.#infiniteObserver = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) btn.click(); },
      { rootMargin: '400px' }
    );
    this.#infiniteObserver.observe(btn);
  }

  // ── Quick Add ──

  async #onQuickAdd(btn) {
    btn.disabled = true;
    btn.classList.add('is-loading');

    try {
      const cartDrawer = document.querySelector('cart-drawer-component');
      const drawerSectionId = cartDrawer?.dataset.sectionId || '';

      const body = JSON.stringify({
        items: [{ id: parseInt(btn.dataset.variantId, 10), quantity: 1 }],
        sections: drawerSectionId,
        sections_url: window.location.pathname,
      });

      const res = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body }));
      const data = await res.json();

      if (!res.ok || data.errors) {
        btn.classList.remove('is-loading');
        btn.disabled = false;
        return;
      }

      document.dispatchEvent(new CartAddEvent(data, null, {
        sections: data.sections,
        source: 'quick-add',
      }));

      btn.classList.remove('is-loading');
      btn.classList.add('is-added');
      setTimeout(() => {
        btn.classList.remove('is-added');
        btn.disabled = false;
      }, 2000);
    } catch (err) {
      console.error('Quick add error:', err);
      btn.classList.remove('is-loading');
      btn.disabled = false;
    }
  }

  // ── Core: fetch + targeted replace ──

  async #fetchAndUpdate(url, { scroll = true } = {}) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    // Fade the grid out, swap while invisible, then fade back in.
    const grid = this.querySelector('[data-product-grid]');
    grid?.classList.add('is-loading');

    try {
      // Hold the fade-out for a beat so it's actually gone before we swap.
      const [html] = await Promise.all([
        this.#fetchSection(url),
        new Promise((resolve) => setTimeout(resolve, 200)),
      ]);
      if (!html) { grid?.classList.remove('is-loading'); return; }
      // Page quittée entre-temps : ne pas réécrire l'URL ni faire défiler la nouvelle page.
      if (!this.isConnected) return;

      const doc = new DOMParser().parseFromString(html, 'text/html');

      // Scroll to top of section, accounting for sticky header
      if (scroll) {
        const headerHeight = parseInt(getComputedStyle(document.body).getPropertyValue('--header-height') || '0', 10);
        const top = this.getBoundingClientRect().top + window.scrollY - headerHeight;
        window.scrollTo({ top, behavior: 'smooth' });
      }

      // Replace ONLY these elements — filter drawer stays open
      this.#replaceElement('[data-product-grid]', doc);
      this.#replaceElement('[data-products-count]', doc);
      this.#replaceElement('[data-active-filters]', doc);
      this.#replaceElement('[data-pagination]', doc);
      this.#replaceElement('[data-empty-state]', doc);

      // Remember which dropdown was open before replacing
      const openIndex = this.#getOpenDropdownIndex();

      // Update inline filter dropdowns + drawer filter form
      this.#replaceElement('[data-inline-filters]', doc);
      this.#replaceElement('[data-drawer-filters]', doc);

      // Re-open the dropdown that was active
      if (openIndex !== -1) {
        const panels = this.querySelectorAll('[data-dropdown-panel]');
        if (panels[openIndex]) panels[openIndex].classList.add('is-open');
      }
      this.#syncExpanded();

      window.history.pushState({}, '', url);

      if (this.paginationType === 'infinite') {
        this.#infiniteObserver?.disconnect();
        this.#initInfiniteScroll();
      }
    } catch (err) {
      if (err.name === 'AbortError') return; // a newer request now owns the grid state
      grid?.classList.remove('is-loading');
      console.error('Collection fetch error:', err);
    }
  }

  #replaceElement(selector, doc) {
    const current = this.querySelector(selector);
    const incoming = doc.querySelector(selector);

    if (current && incoming) {
      current.innerHTML = incoming.innerHTML;
      // Remove attributes no longer present
      for (const attr of [...current.attributes]) {
        if (!incoming.hasAttribute(attr.name)) {
          current.removeAttribute(attr.name);
        }
      }
      // Copy all incoming attributes
      for (const attr of incoming.attributes) {
        current.setAttribute(attr.name, attr.value);
      }
      current.style.opacity = '';
    } else if (current && !incoming) {
      current.remove();
    }
  }

  async #fetchSection(url) {
    const sectionUrl = new URL(url, location.origin);
    sectionUrl.searchParams.set('section_id', this.sectionId);

    const res = await fetch(sectionUrl.toString(), {
      signal: this.#abortController?.signal,
    });
    if (!res.ok) return null;
    return res.text();
  }

  /**
   * Build URL from filter form + sort
   * Shopify price filters expect values in cents (e.g. 1000 for €10)
   */
  #buildURL() {
    const url = new URL(window.location.origin + window.location.pathname);

    // Preserve search query params (q, type) for search pages
    const currentParams = new URLSearchParams(window.location.search);
    if (currentParams.has('q')) url.searchParams.set('q', currentParams.get('q'));
    if (currentParams.has('type')) url.searchParams.set('type', currentParams.get('type'));

    const sort = this.querySelector('[data-sort-select]');
    if (sort?.value) url.searchParams.set('sort_by', sort.value);

    // Checkboxes — only from inline filters to avoid duplicates with drawer
    const filterSource = this.querySelector('[data-inline-filters]') || this;
    filterSource.querySelectorAll('[data-filter-checkbox]:checked').forEach((cb) => {
      url.searchParams.append(cb.name, cb.value);
    });

    // Price range — inputs are in display currency, Shopify needs cents
    const minInput = this.querySelector('[data-input-min]');
    const maxInput = this.querySelector('[data-input-max]');
    const absMin = minInput ? parseFloat(minInput.min) : 0;
    const absMax = maxInput ? parseFloat(maxInput.max) : 0;

    if (minInput?.value && parseFloat(minInput.value) > absMin) {
      url.searchParams.set(minInput.dataset.paramName, Math.round(parseFloat(minInput.value)).toString());
    }
    if (maxInput?.value && parseFloat(maxInput.value) < absMax) {
      url.searchParams.set(maxInput.dataset.paramName, Math.round(parseFloat(maxInput.value)).toString());
    }

    return url.toString();
  }
}

if (!customElements.get('collection-component')) {
  customElements.define('collection-component', CollectionComponent);
}